import {
    Autocomplete,
    Box,
    Divider,
    Stack,
    TextField,
    Typography,
    useColorScheme,
} from '@mui/material';
import {SyntheticEvent, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {setWellNumber} from 'slices/wellNumberSlice';
import {RouteParams} from 'utils';
import {selectWellNumber} from 'slices';
import {useWellsData} from 'utils/hooks/useWellsData';
import {WellStatus} from '@features/wells/types';
import {getChipStylesByStageOrStatus} from '@features/wellCommonDashboard/cards/Cards/CurrentInfoCard/utils';
import {getGroupByStatus, GroupHeader, WellGroupKey} from './utils';
import {useTranslation} from 'react-i18next';

type WellSelectProps = {
    width?: number;
    label?: string;
    variant?: 'standard' | 'outlined';
};

export const WellSelect = ({width = 250, label, variant = 'standard'}: WellSelectProps) => {
    const {t} = useTranslation(['header', 'common']);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const params = useParams<RouteParams>();
    const wellNumber = useSelector(selectWellNumber);

    const [store, updateWellData] = useWellsData();
    const [dynamicWidth, setDynamicWidth] = useState(width);

    const {colorScheme} = useColorScheme();
    const isLightMode = colorScheme === 'light';

    const wellOptions = useMemo(
        () =>
            store?.data?.map((well) => ({
                group: getGroupByStatus(well.status as WellStatus),
                label: `${well.padTitle}/${well.name}/${well.fieldTitle}`,
                value: well.id,
                status: well.status,
            })) ?? [],
        [store?.data],
    );

    const sortedAndGroupedOptions = useMemo(() => {
        const grouped: Record<WellGroupKey, typeof wellOptions> = {
            [WellGroupKey.Active]: [],
            [WellGroupKey.Inactive]: [],
            [WellGroupKey.Completed]: [],
            [WellGroupKey.Planned]: [],
            [WellGroupKey.Other]: [],
        };

        wellOptions.forEach((option) => {
            if (option.group && grouped[option.group]) {
                grouped[option.group].push(option);
            } else {
                grouped[WellGroupKey.Other].push(option);
            }
        });

        return [
            ...grouped[WellGroupKey.Active],
            ...grouped[WellGroupKey.Inactive],
            ...grouped[WellGroupKey.Completed],
            ...grouped[WellGroupKey.Planned],
            ...grouped[WellGroupKey.Other],
        ];
    }, [wellOptions]);

    const currentWell = useMemo(
        () =>
            params.wellId || wellNumber
                ? wellOptions.find(
                      (well) =>
                          (params.wellId || wellNumber) &&
                          well.value === +(params.wellId || wellNumber),
                  )
                : null,
        [params.wellId, wellNumber, wellOptions],
    );

    useEffect(() => {
        updateWellData();
    }, [updateWellData]);

    useEffect(() => {
        if (currentWell?.label) {
            const charWidth = 12;
            const textWidth = currentWell.label.length * charWidth;

            if (textWidth > width) {
                setDynamicWidth(textWidth + 20);
            } else {
                setDynamicWidth(width);
            }
        }
    }, [currentWell, width]);

    const handleWellChange = (
        event: SyntheticEvent<Element, Event>,
        newWell: {label: string; value: number} | null,
    ) => {
        if (newWell) {
            dispatch(setWellNumber(newWell.value));
        }

        if (!newWell) {
            dispatch(setWellNumber(''));
        }

        if (newWell && params.wellId) {
            // we need to navigate to the same page (table/file/calendar) with the same search params but with new wellId
            const newPath = location.pathname
                .replace(params.wellId, `${newWell.value}`)
                .concat(location.search);

            navigate(newPath);
        }
    };

    // Изначальный эффект, чтобы установить значение wellNumber в стор при инициализации
    useEffect(() => {
        if (!!currentWell && !wellNumber) {
            dispatch(setWellNumber(currentWell.value));
        }
    }, [currentWell, dispatch, wellNumber]);

    const labelText = label || t('well', {ns: 'common'});

    return (
        <Autocomplete
            disablePortal
            options={sortedAndGroupedOptions}
            sx={{
                width: `${dynamicWidth}px`,
            }}
            groupBy={(option) => option.group ?? WellGroupKey.Other}
            value={currentWell ?? null}
            getOptionLabel={(option) => option.label}
            onChange={handleWellChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant={variant}
                    label={labelText}
                    size='small'
                />
            )}
            renderGroup={(params) => (
                <li key={params.key}>
                    <GroupHeader>
                        <GroupHeader>{t(params.group as WellGroupKey)}</GroupHeader>
                    </GroupHeader>
                    <Box component='div'>{params.children}</Box>
                </li>
            )}
            renderOption={(props, option) => {
                const {key, ...optionProps} = props;
                return (
                    <li
                        key={key}
                        {...optionProps}
                        style={{width: '100%', padding: 0}}
                    >
                        <Stack
                            sx={{
                                py: 0.5,
                                px: 1,
                                width: '100%',
                            }}
                        >
                            <Typography>{option.label}</Typography>
                            <Typography
                                variant='caption'
                                color={
                                    getChipStylesByStageOrStatus({
                                        stage: option.status || '',
                                        isLightMode,
                                    }).textColor
                                }
                            >
                                {option.status}
                            </Typography>
                            <Divider
                                sx={{
                                    width: '100%',
                                    pb: 0.5,
                                }}
                            />
                        </Stack>
                    </li>
                );
            }}
        />
    );
};
